import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment
} from '@material-ui/core';

import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Autorenew from '@material-ui/icons/Autorenew';

import DuplicateReferenceErrorList from '../JCdTrans/TransDetails/DuplicateReferenceError';
import { DebounceTextField, SingleDatePicker } from 'src/components';
import { useNotif, useRequest, useToggle } from 'src/hooks';
import moment from 'moment';

function UpdateJEVDetails({ jv, setJV, open, onClose }) {
  const [jevNo, setJEVno] = useState('');
  const [jDate, setJDate] = useState('');
  const [RefDate, setRefDate] = useState('');
  const [loading, loadOn, loadOff] = useToggle();
  const [duplicateShow, showDuplicate, hideDuplicate] = useToggle();
  const [errors, setErrors] = useState({});

  const req = useRequest();
  const notif = useNotif();

  async function save() {
    const obligDate =
      (jv?.kvs ?? []).find(item => item.key === 'jv_kvs_oblig')?.value?.jDate ??
      '';

    const payload = {
      jid: jv.jid,
      ixTL: jv.ixTL,
      JEVno: jevNo,
      jDate: moment(jDate).format()
    };

    if (jevNo.trim() === '') {
      notif.error('JEV No is required.');
      return;
    }

    if (jDate === '') {
      notif.error('JEV Date is required.');
      return;
    }

    if (obligDate !== '' && moment(jDate).isBefore(obligDate)) {
      notif.error('JEV Date cannot be earlier than OBR Date');
      return;
    }

    if (!Boolean(jv?.RefDate) && RefDate === '') {
      notif.error('JEV Date is required.');
      return;
    }

    if (!Boolean(jv?.RefDate) && moment(RefDate).isBefore(obligDate)) {
      notif.error('Reference Date Date cannot be earlier than OBR Date');
      return;
    }

    if (!Boolean(jv?.RefDate)) payload.RefDate = RefDate;

    loadOn();
    const { success, error } = await req.put(
      `/trans/jcd/${jv.jcd_uuid}/save`,
      payload
    );
    loadOff();
    setErrors({});

    if (!success) {
      notif.error(error?.data?.msg ?? 'Something went wrong.');
      setErrors(error?.data ?? {});
      return;
    }

    setJV(prev => ({
      ...prev,
      jv: {
        ...prev.jv,
        RefDate: !Boolean(jv?.RefDate)
          ? moment(RefDate).format()
          : prev.jv.RefDate,
        jDate: moment(jDate).format(),
        JEVno: jevNo
      }
    }));
    onClose();
    notif.success('Successfully Saved');
  }

  function onChangeJEVno(e) {
    const { value } = e.target;
    setJEVno(value);
  }

  function onChangeJDate(e) {
    const { value } = e.target;
    setJDate(value);
  }

  function onChangeRefDate(e) {
    const { value } = e.target;
    setRefDate(value);
  }

  async function generateJEVno() {
    loadOn();
    const { success, data } = await req.get(`/trans/get-jevno`);
    loadOff();

    if (!success) {
      notif.error('Failed to get JEV No');
      return;
    }

    setJEVno(data);
  }

  useEffect(() => {
    if (open) {
      setJEVno(jv?.JEVno ?? '');
      setJDate(jv?.jDate ?? '');

      if (!Boolean(jv?.RefDate)) setRefDate(jv?.jDate);

      setErrors({});
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h4" color="textSecondary">
          Update
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box mb={1}>
          <DebounceTextField
            value={jevNo}
            onChange={onChangeJEVno}
            fullWidth
            label="JEV #"
            variant="outlined"
            error={Boolean(errors?.item?.validation_errors?.JEVno)}
            helperText={errors?.item?.validation_errors?.JEVno}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title="Generate">
                    <IconButton
                      edge="start"
                      onClick={generateJEVno}
                      disabled={loading || jv?.JEVno !== ''}
                    >
                      <Autorenew />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
              endAdornment: (errors?.item?.duplicate_JEVno ?? []).length >
                0 && (
                <InputAdornment position="end">
                  <Tooltip title="Duplicates">
                    <IconButton
                      aria-label="view duplicates"
                      edge="end"
                      onClick={showDuplicate}
                    >
                      <ErrorOutlineIcon color="error" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </Box>
        <SingleDatePicker
          value={jDate}
          onChange={onChangeJDate}
          fullWidth
          label="JEV Date"
          error={Boolean(errors?.item?.validation_errors?.jDate)}
          helperText={errors?.item?.validation_errors?.jDate}
        />

        {!Boolean(jv?.RefDate) && (
          <Box mt={1}>
            <SingleDatePicker
              value={RefDate}
              onChange={onChangeRefDate}
              fullWidth
              label="Ref Date"
              error={Boolean(errors?.item?.validation_errors?.RefDate)}
              helperText={errors?.item?.validation_errors?.RefDate}
            />
          </Box>
        )}

        <DuplicateReferenceErrorList
          open={duplicateShow}
          duplicates={errors?.item?.duplicate_JEVno ?? []}
          handleClose={hideDuplicate}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={loading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UpdateJEVDetails;
